import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { RedBold } from 'components/Containers';
import { INBOUND_REFERRALS_ROUTES } from 'components/inboundReferrals/constants';
import { ArchivableLabel } from 'components/patient/blurbs/activities/Layout';
import { Flags } from 'constants/flags';
import { useProfile } from 'context/profile';
import { useTranslation } from 'context/translations';
import { dateWithoutTimezone } from 'lib/date';
import ArchiveLocationEpisode from 'models/activities/ArchiveLocationEpisode';
import DischargeQuestions from 'models/activities/DischargeQuestions';
import RehabStateChange from 'models/activities/RehabStateChange';
import Activity, { ActivityType } from 'models/Activity';
import { PRODUCT_ACCESSORS } from 'models/Profile';
import { RehabStateApiName } from 'models/RehabState';
import { colors } from 'styles/theme/colors';
import { Label, LabelBold } from 'styles/typography';

type NoteContentHeaderProps = {
  activity: Activity;
};

const NoteContentHeader = (props: NoteContentHeaderProps) => {
  const { activity } = props;
  const { translate, translateAll } = useTranslation();
  const { profile } = useProfile();

  if (activity instanceof RehabStateChange) {
    const currentRehabStateApiName = activity.data.currentRehabState.apiName;

    // A queue entry created by a Selected inbound referral (OLIO-2666) reads as one
    // sentence linking back to the referral, replacing the "by <user>" attribution.
    // The link only renders when the reader could actually open it: the route needs the
    // product accessor AND the flag (same pairing as SideNavbar), and the referral belongs
    // to one client while the story is shared across them — a reader acting as a different
    // client would get "Referral not found". Everyone else gets the sentence as plain text.
    if (currentRehabStateApiName === RehabStateApiName.Queue && activity.data.referralWorkflowId) {
      const canOpenReferral =
        profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) &&
        profile.hasFlag(Flags.InboundReferrals) &&
        (!activity.data.referralClientId || activity.data.referralClientId === profile.actingClient.id);
      const referralText = canOpenReferral ? (
        <ReferralLink to={`${INBOUND_REFERRALS_ROUTES.BASE}/${activity.data.referralWorkflowId}`}>
          accepted referral
        </ReferralLink>
      ) : (
        'accepted referral'
      );
      return (
        <ArchivableLabel $archived={activity.archived}>
          {activity.patient?.name} added to the {translate('Queue').toLowerCase()} at {activity.data.rehabFacility.name}{' '}
          via an {referralText} on {dateWithoutTimezone(activity.enteredAt!)}
        </ArchivableLabel>
      );
    }
    const rehabFacilityText =
      currentRehabStateApiName === RehabStateApiName.Discharged ? '' : `${activity.data.rehabFacility.name} by `;
    let rehabStateText = {
      [RehabStateApiName.Queue]: `added to ${translate('Queue')} at`,
      admission: 'admitted to', // maintain for backwards compatibility
      [RehabStateApiName.InTreatment]: `moved to ${translate(activity.data.currentRehabState.state)} at`,
      [RehabStateApiName.Discharged]: '<>',
    }[currentRehabStateApiName];
    if (currentRehabStateApiName === RehabStateApiName.Discharged) {
      const reason = activity.data.dischargeInfo.reason || '';

      const dischargeReason = reason.charAt(0).toLowerCase() + reason.slice(1);
      const dischargeGroup = activity.data.dischargeInfo.dischargedTo;
      const againstMedicalAdvice = activity.data.dischargeInfo.againstMedicalAdvice;

      const dischargeReasonText =
        (dischargeReason || `${translate('discharged').toLowerCase()}; no reason available`) +
        (againstMedicalAdvice ? ' (against medical advice)' : '');
      const dischargeGroupText = dischargeGroup ? `to ${dischargeGroup} ` : '';

      rehabStateText = `was ${dischargeReasonText}. ${translate('Discharged')} from ${activity.data.rehabFacility.name} ${dischargeGroupText}by`;
    }
    return (
      <ArchivableLabel $archived={activity.archived}>
        {activity.patient?.name} {rehabStateText} {rehabFacilityText}
        <LabelBold> {activity.createdBy.fullName} </LabelBold>
        on {dateWithoutTimezone(activity.enteredAt!)}
      </ArchivableLabel>
    );
  }
  if (activity instanceof ArchiveLocationEpisode) {
    return (
      <Label>
        {activity.patient?.name} record at {activity.data.rehabFacility.name} was canceled by{' '}
        <LabelBold>{activity.createdBy.fullName}</LabelBold> on {dateWithoutTimezone(activity.createdAt!)}
      </Label>
    );
  }
  return (
    <Label>
      {activity instanceof DischargeQuestions && !activity.archived && activity.edited && <i>(Edited) </i>}
      {activity.escalation?.isDefault ? (
        <RedBold>Escalation {translateAll(activity.typeLabel)}</RedBold>
      ) : (
        translateAll(activity.typeLabel)
      )}
      {!!activity.attachments?.length && ' (with attachments)'}
      {[
        ActivityType.IMPORT_PATIENT_FIELD_CHANGES,
        ActivityType.PATIENT_FIELD_CHANGES,
        ActivityType.REFERRAL_DEMOGRAPHICS_UPDATES,
      ].includes(activity.type)
        ? ' updated by '
        : ' added by '}
      <LabelBold>{activity.createdBy.fullName}</LabelBold>
      {activity.createdByGroup?.name && ` at ${activity.createdByGroup.name}`}
    </Label>
  );
};

const ReferralLink = styled(Link)`
  color: ${colors.blue100};
`;

export default NoteContentHeader;
